import {
  Box,
  Typography,
  Button,
  Avatar,
  TextField,
  Card,
  Stack,
  Divider,
} from '@mui/material';
import { CloudUpload, Image, RestartAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getIconSrc } from '../../../utils/getIconSrc';

interface IconItem {
  name: string;
  key: string;
  category: string;
  type: string;
  editableFields: {
    icon: boolean;
    description: boolean;
    name: boolean;
  };
  current: {
    icon: string;
    description: string;
    name?: string;
    url: string;
  };
}

interface IconsTabMobileProps {
  filteredAndSortedItems: IconItem[];
  editingDescription: string | null;
  editingName: string | null;
  onStartEditingName: (category: string, key: string) => void;
  onFinishEditingName: (category: string, key: string, name: string) => void;
  onCancelEditingName: () => void;
  impersonationLock: boolean;
  resettingItems: Set<string>;
  onStartEditing: (category: string, key: string) => void;
  onFinishEditing: (category: string, key: string, description: string) => void;
  onCancelEditing: () => void;
  onIconChange: (category: string, key: string, file: File) => void;
  onResetIcon: (category: string, key: string) => void;
  getIconFieldError: (
    category: string,
    iconKey: string
  ) => string[] | undefined;
  baseAssetUrl: string;
}

export const IconsTabMobile = ({
  filteredAndSortedItems,
  editingDescription,
  editingName,
  onStartEditingName,
  onFinishEditingName,
  onCancelEditingName,
  impersonationLock,
  resettingItems,
  onStartEditing,
  onFinishEditing,
  onCancelEditing,
  onIconChange,
  onResetIcon,
  getIconFieldError,
  baseAssetUrl,
}: IconsTabMobileProps) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={12} sx={{ mt: 24 }}>
      {filteredAndSortedItems.map((item) => {
        const icon = item.current?.icon || '';
        const iconName = item.current?.name || item.name || item.key;
        const iconAlt = t('branding.iconsTab.iconAlt', {
          name: iconName,
        });
        const iconSrc = getIconSrc(icon, baseAssetUrl);

        return (
          <Card
            variant="primary"
            key={`${item.category}-${item.key}`}
            sx={{
              '&:hover': { cursor: 'default' },
            }}
          >
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  rowGap: 8,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    minWidth: 0,
                  }}
                >
                  <Typography variant="caption">
                    {t('branding.iconsTab.tableHeaders.icon')}
                  </Typography>
                  {iconSrc ? (
                    <Box
                      component="span"
                      role="img"
                      aria-label={iconAlt}
                      sx={{
                        width: 80,
                        height: 80,
                        display: 'block',
                      }}
                    >
                      <img
                        src={iconSrc}
                        alt=""
                        aria-hidden="true"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: 4,
                          display: 'block',
                        }}
                      />
                    </Box>
                  ) : (
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: 'var(--color-greyish-blue)',
                      }}
                      role="img"
                      aria-label={iconAlt}
                    >
                      <Image
                        fontSize="large"
                        sx={{ color: 'var(--color-dark-greyish-blue)' }}
                        aria-hidden="true"
                      />
                    </Avatar>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 4,
                    alignItems: 'center',
                    flexShrink: 0,
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    variant="default"
                    size="small"
                    startIcon={<CloudUpload aria-hidden />}
                    disabled={impersonationLock || !item.editableFields.icon}
                    aria-label={`Upload icon for ${item.name}`}
                  >
                    {t('branding.iconsTab.uploadButton')}
                    <input
                      type="file"
                      accept=".png,.svg"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          onIconChange(
                            item.category,
                            item.key,
                            e.target.files[0]
                          );
                        }
                      }}
                      hidden
                      disabled={!item.editableFields.icon}
                      aria-label={`Upload icon for ${item.name}`}
                    />
                  </Button>
                  {!item.current.icon?.includes('assets') &&
                    item.editableFields.icon && (
                      <Button
                        variant="link"
                        size="small"
                        onClick={() => onResetIcon(item.category, item.key)}
                        disabled={
                          impersonationLock ||
                          resettingItems.has(`${item.category}-${item.key}`) ||
                          !item.editableFields.icon
                        }
                        startIcon={<RestartAlt aria-hidden />}
                        title={t('branding.iconsTab.resetTooltip') as string}
                        aria-label={`Reset icon for ${item.name}`}
                      >
                        {t('branding.iconsTab.resetTooltip')}
                      </Button>
                    )}
                </Box>
              </Box>
              <Divider />

              <Box>
                <Typography variant="caption">
                  {t('branding.iconsTab.tableHeaders.name')}
                </Typography>
                {item.editableFields.name &&
                editingName === `${item.category}-${item.key}` ? (
                  <TextField
                    defaultValue={item.current.name || item.name}
                    onBlur={(e) =>
                      onFinishEditingName(
                        item.category,
                        item.key,
                        e.target.value
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onFinishEditingName(
                          item.category,
                          item.key,
                          (e.target as HTMLInputElement).value
                        );
                      } else if (e.key === 'Escape') {
                        onCancelEditingName();
                      }
                    }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    autoFocus
                    sx={{
                      mt: 0.5,
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                        fontSize: '14px',
                        textTransform: 'capitalize',
                      },
                    }}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 0.5,
                      textTransform: 'capitalize',
                      cursor:
                        item.editableFields.name && !impersonationLock
                          ? 'pointer'
                          : 'default',
                      '&:hover':
                        item.editableFields.name && !impersonationLock
                          ? {
                              backgroundColor:
                                'var(--color-light-greyish-blue)',
                              borderRadius: 1,
                              padding: '2px 4px',
                              margin: '-2px -4px',
                            }
                          : {},
                    }}
                    onClick={
                      item.editableFields.name && !impersonationLock
                        ? () => onStartEditingName(item.category, item.key)
                        : undefined
                    }
                  >
                    {item.name}
                  </Typography>
                )}
              </Box>
              <Divider />

              <Box>
                <Typography variant="caption">
                  {t('branding.iconsTab.tableHeaders.type')}
                </Typography>
                <Typography variant="body1">{item.type}</Typography>
              </Box>
              <Divider />

              <Box>
                <Typography variant="caption">
                  {t('branding.iconsTab.tableHeaders.description')}
                </Typography>
                {item.editableFields.description &&
                editingDescription === `${item.category}-${item.key}` ? (
                  <TextField
                    defaultValue={item.current.description}
                    placeholder={
                      t('branding.iconsTab.descriptionPlaceholder') as string
                    }
                    onBlur={(e) =>
                      onFinishEditing(item.category, item.key, e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onFinishEditing(
                          item.category,
                          item.key,
                          (e.target as HTMLInputElement).value
                        );
                      } else if (e.key === 'Escape') {
                        onCancelEditing();
                      }
                    }}
                    size="small"
                    fullWidth
                    autoFocus
                    multiline
                    rows={3}
                    error={!!getIconFieldError(item.category, item.key)}
                    helperText={getIconFieldError(item.category, item.key)?.[0]}
                    sx={{
                      mt: 0.5,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: getIconFieldError(
                            item.category,
                            item.key
                          )
                            ? 'var(--color-dark-greyish-blue)'
                            : 'var(--color-scifree-teal)',
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                        fontSize: '14px',
                      },
                    }}
                  />
                ) : (
                  <Box
                    onClick={
                      item.editableFields.description && !impersonationLock
                        ? () => onStartEditing(item.category, item.key)
                        : undefined
                    }
                    onKeyDown={(e) => {
                      if (
                        item.editableFields.description &&
                        !impersonationLock &&
                        (e.key === 'Enter' || e.key === ' ')
                      ) {
                        e.preventDefault();
                        onStartEditing(item.category, item.key);
                      }
                    }}
                    tabIndex={
                      item.editableFields.description && !impersonationLock
                        ? 0
                        : -1
                    }
                    sx={{
                      mt: 0.5,
                      minHeight: 40,
                      padding: '8px 12px',
                      cursor:
                        item.editableFields.description && !impersonationLock
                          ? 'pointer'
                          : 'default',
                      borderRadius: 1,
                      border: '1px solid var(--color-greyish-blue)',
                      '&:hover':
                        item.editableFields.description && !impersonationLock
                          ? {
                              backgroundColor: 'var(--color-greyish-blue)',
                              border: '1px solid',
                              borderColor: 'var(--color-dark-greyish-blue)',
                            }
                          : {},
                      '&:focus-visible':
                        item.editableFields.description && !impersonationLock
                          ? {
                              outline: '2px solid',
                              outlineColor: 'var(--color-scifree-teal)',
                              outlineOffset: '1px',
                            }
                          : {},
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    role={
                      item.editableFields.description && !impersonationLock
                        ? 'button'
                        : 'text'
                    }
                    aria-label={
                      item.editableFields.description && !impersonationLock
                        ? `Edit description for ${item.name}`
                        : undefined
                    }
                  >
                    {item.current.description ? (
                      <Typography variant="body1">
                        {item.current.description}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontStyle: 'italic',
                        }}
                      >
                        {t('branding.iconsTab.descriptionPlaceholder')}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
};
